import { DestroyRef, computed, inject, Injectable, Injector, signal } from '@angular/core';
import { Router } from '@angular/router';
import { EventqToastService } from '@ecam/eventq-components';
import {
  clearSharedSession,
  persistSessionExpiresAt,
  readSessionExpiresAt,
  readSharedSession,
  writeSharedSession,
} from '../auth/shared-session.storage';
import { readJwtExpiryMs } from '../auth/jwt-claims.util';
import { applyDevRoleOverride } from '../config/eventq-role-override.config';
import type { AgentPermissions, AgentRole } from '../models/entities/agent-role.model';
import { TAB_EVENTS_PAGE, TAB_WAITING_PAGE } from '../routing/browser-tab-routes';
import { AgentAccessService } from './agent-access.service';
import { AgentDataSyncService } from './agent-data-sync.service';
import { AgentDataService } from './agent-data.service';
import { AgentEventsService } from './agent-events.service';
import { AgentPreferencesService } from './agent-preferences.service';
import { BrowserTabService } from './browser-tab.service';
import { EventHandleService } from './event-handle.service';
import { MockRuntimeConfigService } from './mock-runtime-config.service';
import { ProcessingTimerService } from './processing-timer.service';

export {
  ACCESS_TOKEN_STORAGE_KEY,
  SESSION_DISPLAY_NAME_STORAGE_KEY,
  SESSION_EMAIL_STORAGE_KEY,
  SESSION_EXPIRES_AT_STORAGE_KEY,
  SESSION_ROLE_STORAGE_KEY,
} from '../auth/shared-session.storage';

/** Warn this far before STS `expiresAt`. */
export const SESSION_EXPIRY_WARNING_MS = 15 * 60_000;
/** Auto-logout this far before STS `expiresAt` (typical STS TTL is 86400s). */
export const SESSION_EXPIRY_LOGOUT_LEAD_MS = 5 * 60_000;
const SESSION_EXPIRY_WARNING_TITLE = 'Session expiring';
const SESSION_EXPIRY_WARNING_SUBTITLE =
  'Please save your work. You can sign out and sign back in. You will be signed out soon.';
/** Dealer user id from STS — local only; sibling apps do not need it. */
export const SESSION_USER_ID_STORAGE_KEY = 'eventq:sessionUserId';

export interface EstablishSessionOptions {
  persist?: boolean;
  role?: AgentRole | null;
  displayName?: string | null;
  /** Numeric Dealer user id from the STS exchange; identifies work this agent owns. */
  userId?: number | null;
  /** Authoritative permissions from backend when `useBackendPermissions` is enabled. */
  permissions?: AgentPermissions | null;
  /** When false, skip BroadcastChannel SESSION_LOGIN (remote restore). Default true. */
  emitSessionLogin?: boolean;
  /** STS `expireInSeconds` / `expires_in`. Omitted for mock/email login (no auto-logout). */
  expiresIn?: number;
  /** Keep a previously stored `sessionExpiresAt` (restore / sibling sync). */
  keepExpiresAt?: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly router = inject(Router);
  private readonly browserTabs = inject(BrowserTabService);
  private readonly dataSync = inject(AgentDataSyncService);
  private readonly preferences = inject(AgentPreferencesService);
  private readonly injector = inject(Injector);
  private readonly destroyRef = inject(DestroyRef);
  private readonly toast = inject(EventqToastService);
  private destroyed = false;
  private expiryWarningShown = false;
  private expiryLogoutInFlight = false;

  readonly accessToken = signal<string | null>(null);
  readonly email = signal<string | null>(null);
  /** Real signed-in role (what Okta/STS stored). UI reads `role` after the dev override. */
  readonly sessionRole = signal<AgentRole | null>(null);
  readonly role = computed(() => applyDevRoleOverride(this.sessionRole()));
  readonly displayName = signal<string | null>(null);
  readonly userId = signal<number | null>(null);
  readonly backendPermissions = signal<AgentPermissions | null>(null);
  readonly isAuthenticated = computed(() => this.accessToken() !== null);

  readonly shouldManageEvents = computed(() => {
    const role = this.role();
    return !!role && role !== 'Escalation';
  });

  constructor() {
    const timerId = window.setInterval(() => {
      if (this.destroyed) {
        return;
      }
      void this.syncFromSharedSession();
    }, 1000);
    const onVisibility = () => {
      if (!this.destroyed) {
        this.evaluateSessionExpiry();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    this.destroyRef.onDestroy(() => {
      this.destroyed = true;
      window.clearInterval(timerId);
      document.removeEventListener('visibilitychange', onVisibility);
    });
  }

  restoreFromStorage(): void {
    const session = readSharedSession();
    if (!session) {
      return;
    }

    if (isSessionLogoutDue(readSessionExpiresAt())) {
      this.logout();
      return;
    }

    this.establishSession(session.accessToken, session.email ?? undefined, {
      persist: true,
      role: (session.role as AgentRole | null) ?? null,
      displayName: session.displayName,
      userId: readStoredUserId(),
      emitSessionLogin: false,
      keepExpiresAt: true,
    });
  }

  establishSession(
    accessToken: string,
    email?: string,
    options: EstablishSessionOptions = { persist: true },
  ): void {
    this.accessToken.set(accessToken);
    if (email) {
      this.email.set(email);
    }
    if (options.role !== undefined) {
      this.sessionRole.set(options.role);
    }
    if (options.displayName !== undefined) {
      this.displayName.set(options.displayName);
    }
    if (options.userId !== undefined) {
      this.userId.set(options.userId);
    }
    if (options.permissions !== undefined) {
      this.backendPermissions.set(options.permissions);
    }

    if (options.persist !== false) {
      // Persist the real claim, never the `devRoleOverride` value that `role()` exposes.
      writeSharedSession({
        accessToken,
        email: email ?? this.email(),
        role: options.role ?? this.sessionRole(),
        displayName: options.displayName ?? this.displayName(),
      });
      persistUserId(options.userId !== undefined ? options.userId : this.userId());
      this.persistExpiresAtFromOptions(options, accessToken);
    }

    this.dataSync.connect(accessToken);

    if (options.emitSessionLogin !== false) {
      this.injector.get(AgentEventsService).broadcastSessionLogin();
    }

    void this.injector.get(MockRuntimeConfigService).refresh();
    this.evaluateSessionExpiry();
  }

  /**
   * Sibling signed in elsewhere: ensure local session matches shared storage
   * and leave /login for the authenticated shell.
   */
  async applyRemoteSession(): Promise<void> {
    const session = readSharedSession();
    if (!session) {
      return;
    }

    const sameToken = this.accessToken() === session.accessToken;
    if (!sameToken) {
      this.restoreFromStorage();
    }
    if (!this.isAuthenticated()) {
      return;
    }

    const url = this.router.url.split('?')[0] ?? '';
    if (!url.includes('/login')) {
      return;
    }

    const access = this.injector.get(AgentAccessService);
    const landing = access.canOpenEventsDashboard() ? TAB_EVENTS_PAGE : TAB_WAITING_PAGE;
    await this.router.navigate(['/t1', landing], { replaceUrl: true });
  }

  /**
   * Sibling signed out elsewhere. Clear local auth only — do not call logout API
   * or re-broadcast (the peer already did).
   */
  applyRemoteLogout(): void {
    if (!this.isAuthenticated()) {
      return;
    }
    this.clearLocalSession();
    void this.router.navigate(['/login']);
  }

  private async syncFromSharedSession(): Promise<void> {
    this.evaluateSessionExpiry();
    const session = readSharedSession();
    if (!session) {
      this.applyRemoteLogout();
      return;
    }
    await this.applyRemoteSession();
  }

  /**
   * Another browser took this user's session. Clear local auth only —
   * do not call logout API or release open work (server keeps holds within timeout).
   */
  handleSessionSuperseded(): void {
    if (!this.isAuthenticated()) {
      return;
    }
    this.clearLocalSession();
    void this.router.navigate(['/login'], { queryParams: { reason: 'signed-in-elsewhere' } });
  }

  /**
   * Gateway rejected the Stealth JWT (expired or otherwise unauthorized).
   * Clear local auth only — do not call logout API or release open work.
   */
  handleSessionExpired(): void {
    if (!this.isAuthenticated()) {
      return;
    }
    this.clearLocalSession();
    void this.router.navigate(['/login'], { queryParams: { reason: 'session-expired' } });
  }

  /**
   * Mock API process restart or `/api/test/reset`. Clear local workspace and re-auth —
   * do not call logout API or release server work (store is already empty).
   */
  handleMockStoreReset(): void {
    if (!this.isAuthenticated()) {
      return;
    }
    this.clearLocalSession();
    void this.router.navigate(['/login'], { queryParams: { reason: 'server-restarted' } });
  }

  private persistExpiresAtFromOptions(options: EstablishSessionOptions, accessToken: string): void {
    if (options.keepExpiresAt) {
      return;
    }
    const seconds = options.expiresIn;
    const fromSts =
      typeof seconds === 'number' && Number.isFinite(seconds) && seconds > 0
        ? Date.now() + seconds * 1000
        : null;
    const fromJwt = readJwtExpiryMs(accessToken);
    const candidates = [fromSts, fromJwt].filter((value): value is number => value != null);
    persistSessionExpiresAt(candidates.length > 0 ? Math.min(...candidates) : null);
    this.expiryWarningShown = false;
  }

  private evaluateSessionExpiry(): void {
    if (!this.isAuthenticated() || this.expiryLogoutInFlight) {
      return;
    }
    const expiresAt = readSessionExpiresAt();
    if (expiresAt == null) {
      return;
    }
    const now = Date.now();
    if (now >= expiresAt - SESSION_EXPIRY_LOGOUT_LEAD_MS) {
      this.expiryLogoutInFlight = true;
      this.logout();
      this.expiryLogoutInFlight = false;
      return;
    }
    if (now >= expiresAt - SESSION_EXPIRY_WARNING_MS) {
      this.showExpiryWarning();
    }
  }

  private showExpiryWarning(): void {
    if (this.expiryWarningShown) {
      return;
    }
    this.expiryWarningShown = true;
    this.toast.show('notice', {
      title: SESSION_EXPIRY_WARNING_TITLE,
      subtitle: SESSION_EXPIRY_WARNING_SUBTITLE,
      durationMs: 0,
    });
  }

  private clearLocalSession(): void {
    this.expiryWarningShown = false;
    // A shared-session poll can land after the testing injector is destroyed.
    try {
      this.injector.get(ProcessingTimerService, null, { optional: true })?.completeAll('logout');
    } catch {
      return;
    }
    this.accessToken.set(null);
    this.email.set(null);
    this.sessionRole.set(null);
    this.displayName.set(null);
    this.userId.set(null);
    this.backendPermissions.set(null);
    clearSharedSession();
    persistUserId(null);
    this.dataSync.disconnect();
    this.browserTabs.reset();
    this.preferences.resetAutoProcess();
  }

  logout(options?: { skipApi?: boolean }): void {
    if (!this.injector.get(MockRuntimeConfigService).resumeWorkAfterLogout()) {
      this.injector.get(EventHandleService).releaseAllForCurrentUser();
    }
    if (this.isAuthenticated()) {
      this.injector.get(AgentEventsService).broadcastSessionLogout();
    }
    this.clearLocalSession();
    if (!options?.skipApi) {
      void this.injector.get(AgentDataService).logout().subscribe({ error: () => undefined });
    }
    void this.router.navigate(['/login']);
  }
}

/** Sessions stored before the user id was captured restore without one. */
function readStoredUserId(): number | null {
  const stored = localStorage.getItem(SESSION_USER_ID_STORAGE_KEY);
  if (!stored) {
    return null;
  }
  const parsed = Number(stored);
  return Number.isInteger(parsed) ? parsed : null;
}

function persistUserId(userId: number | null): void {
  if (userId == null) {
    localStorage.removeItem(SESSION_USER_ID_STORAGE_KEY);
    return;
  }
  localStorage.setItem(SESSION_USER_ID_STORAGE_KEY, String(userId));
}

function isSessionLogoutDue(expiresAt: number | null): boolean {
  return expiresAt != null && Date.now() >= expiresAt - SESSION_EXPIRY_LOGOUT_LEAD_MS;
}
